import React, { useState, useContext, useEffect, useMemo } from 'react'
import ResponsiveAppBar from '../../components/Navbar'
import { getList } from '../../services/admin'
import { UserContext } from '../../context/UserProvider.jsx'
import { DataGrid } from '@mui/x-data-grid'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import {
  Button,
  FormGroup,
  TextField,
  withStyles,
  Chip,
} from '@material-ui/core'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import Select from 'react-select'


const StyledTextField = withStyles({
  root: {
    backgroundColor: '#fff',
    flex: 1,
    minWidth: 280,
  },
})(TextField)

const accessFilterStyles = {
  container: (base) => ({
    ...base,
    minWidth: 240,
    flex: '0 0 240px',
  }),
  control: (base) => ({
    ...base,
    minHeight: 56,
    borderRadius: 4,
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: 12,
    paddingRight: 12,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
  }),
}

const formatAccessLabel = (key) => key.toLocaleUpperCase()

const chipContainerSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.75,
  width: '100%',
  py: 1,
}

const columns = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'name', headerName: 'Name', minWidth: 220, flex: 0.9 },
  { field: 'email', headerName: 'Email', minWidth: 260, flex: 1 },
  {
    field: 'access',
    headerName: 'Access',
    minWidth: 440,
    flex: 1.8,
    renderCell: (params) => {
      const access = Object.entries(params.row.access?.control ?? {})
        .filter(([, item]) => item)
        .sort(([left], [right]) => formatAccessLabel(left).localeCompare(formatAccessLabel(right)))

      return (
        <Box sx={chipContainerSx}>
          {access.map(([key]) => (
            <Chip
              key={key}
              label={formatAccessLabel(key)}
              sx={{
                height: 'auto',
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'normal',
                  lineHeight: 1.25,
                  py: 0.75,
                  wordBreak: 'break-word',
                  textAlign: 'left',
                },
              }}
            />
          ))}
        </Box>
      )
    },
  },
  {
    field: 'notifications',
    headerName: 'Notification',
    minWidth: 280,
    flex: 1.1,
    renderCell: (params) => {
      const notifications = Object.entries(params.row.access?.notifications ?? {})
        .filter(([, item]) => item)
        .sort(([left], [right]) => formatAccessLabel(left).localeCompare(formatAccessLabel(right)))

      return (
        <Box sx={chipContainerSx}>
          {notifications.map(([key]) => (
            <Chip
              key={key}
              label={formatAccessLabel(key)}
              sx={{
                height: 'auto',
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'normal',
                  lineHeight: 1.25,
                  py: 0.75,
                  wordBreak: 'break-word',
                  textAlign: 'left',
                },
              }}
            />
          ))}
        </Box>
      )
    },
  },
]

const App = () => {
  const navigate = useNavigate()
  const { token } = useContext(UserContext)
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessFilter, setAccessFilter] = useState('')
  const [searchValue, setSearchValue] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = () => {
    setLoading(true)
    getList(token, {
      'user-specific': false,
      'record-per-page': 'all',
    }).then((res) => {
      if (res.status && res.status !== 200) {
        alert(res.data?.message || 'Unable to load users.')
        setRows([])
        setLoading(false)
        return
      }

      const summary = []
      ;(res.collection ?? []).forEach((item) => {
        summary.push({
          id: item.raw.id,
          access: {
            control: item.data?.access?.control ?? {},
            notifications: item.data?.access?.notifications ?? {},
          },
          ...item.data,
        })
      })
      summary.sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''))
      setRows(summary)
      setLoading(false)
    })
  }

  const accessOptions = useMemo(() => {
    const labels = new Set()
    ;(rows ?? []).forEach((row) => {
      Object.entries(row.access?.control ?? {}).forEach(([key, enabled]) => {
        if (enabled) {
          labels.add(key)
        }
      })
    })

    return Array.from(labels)
      .sort((left, right) => formatAccessLabel(left).localeCompare(formatAccessLabel(right)))
      .map((value) => ({
        value,
        label: formatAccessLabel(value),
      }))
  }, [rows])

  const filteredRows = useMemo(() => {
    const normalizedSearchValue = searchValue.trim().toLocaleLowerCase()

    return (rows ?? []).filter((row) => {
      const matchesEmail = !normalizedSearchValue || (row.email ?? '').toLocaleLowerCase().includes(normalizedSearchValue)
      const matchesAccess = !accessFilter || Boolean(row.access?.control?.[accessFilter])

      return matchesEmail && matchesAccess
    })
  }, [rows, accessFilter, searchValue])

  const handleRecordSelect = (row) => {
    navigate(`/users/${row.id}/information`)
  }

  if (!rows) {
    return null
  }


  return (
    <>
      <ResponsiveAppBar />
      <Container maxWidth={'xl'}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <h1>User Management</h1>
          </Grid>
          <Grid item xs={12} md={8}>
            <FormGroup
              row
              style={{
                marginTop: '20px',
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'stretch',
                gap: '16px',
              }}
            >
              <Select
                placeholder="Filter By Access"
                isClearable
                options={accessOptions}
                value={accessOptions.find((option) => option.value === accessFilter) ?? null}
                onChange={(option) => setAccessFilter(option?.value ?? '')}
                styles={accessFilterStyles}
              />
              <StyledTextField
                variant="outlined"
                placeholder="Search Email"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
              <Button
                variant="contained"
                disableElevation
                onClick={() => {
                  navigate('/users/create')
                }}
                style={{
                  backgroundColor: '#3675af',
                  color: 'white',
                  height: '56px',
                  minWidth: '120px',
                  flexShrink: 0,
                }}
                fullWidth={isMobile}
              >
                Create
              </Button>
            </FormGroup>
          </Grid>
        </Grid>

        <div
          style={{
            height: '80vh',
            width: '100%',
            backgroundColor: '#ffff',
            marginTop: '20px',
          }}
        >
          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
              pagination: {
                paginationModel: {
                  pageSize: 25,
                  page: 0,
                },
              },
            }}
            getRowHeight={() => 'auto'}
            pagination
            loading={loading}
            disableColumnFilter
            disableColumnMenu
            onRowClick={handleRecordSelect}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              '& .MuiDataGrid-cell': {
                alignItems: 'flex-start',
                py: 0.5,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
            }}
          />
        </div>
      </Container>
    </>
  )
}

export default App
